import type { NavigationFailure, RouteLocationNormalized, Router } from 'vue-router'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

/**
 * Google Analytics page tracking (D-08; legacy `vue-gtag` with router tracking, `main.js:66-68`). Loaded only when the
 * build sets `VITE_GA_MEASUREMENT_ID` (kept in the host's `.env`).
 *
 * A page view is a new page: a navigation that stays on the same path — a list writing its filters, search, sort or
 * page to the query (`useListQuery` `urlSync`), the AI chat setting them — is not one, nor is a navigation that
 * failed or was aborted. The first navigation always counts.
 */
export function isPageView(
  to: Pick<RouteLocationNormalized, 'path'>,
  from: Pick<RouteLocationNormalized, 'path' | 'matched'>,
  failure?: NavigationFailure,
): boolean {
  if (failure) return false
  return from.matched.length === 0 || to.path !== from.path
}

export function installAnalytics(router: Router, measurementId: string | null): void {
  if (!measurementId || typeof document === 'undefined') return
  window.dataLayer = window.dataLayer ?? []
  window.gtag = function gtag() {
    // gtag.js reads the `arguments` object itself.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments)
  }
  window.gtag('js', new Date())
  window.gtag('config', measurementId, { send_page_view: false })
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
  document.head.appendChild(script)
  router.afterEach((to, from, failure) => {
    if (!isPageView(to, from, failure || undefined)) return
    window.gtag?.('event', 'page_view', { page_path: to.path, page_location: window.location.href })
  })
}
